<template>
  <div class="booking-confirmation-timeout">
    <div class="container-fluid">
      <div class="row no-gutters">
        <div class="col-12 col-lg-8 offset-lg-2">
          <div class="booking-confirmation-timeout__interaction">
            <CiInfoBox :type="'warning'" v-if="confirmation.refunded">
              <h3 class="booking__headline">{{ $t('pages.bookingConfirmationTimeout.refundSuccess.infoBox.title') }}</h3>
              <p v-html="$t('pages.bookingConfirmationTimeout.refundSuccess.infoBox.copy')"></p>
              <CiButton class="navigation__button button--primary" @click="startOver()">
                <span>{{ $t('pages.bookingConfirmationTimeout.refundSuccess.infoBox.button') }}</span>
                <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
              </CiButton>
            </CiInfoBox>
            <CiInfoBox :type="'warning'" v-else>
              <h3 class="booking__headline">{{ $t('pages.bookingConfirmationTimeout.refundNoSuccess.infoBox.title') }}</h3>
              <p v-html="$t('pages.bookingConfirmationTimeout.refundNoSuccess.infoBox.copy', {booking_number: confirmation.booking_number, support_email: supportEmail, email_subject: getRefundNoSuccessEmailSubject()})"></p>
            </CiInfoBox>

            <h2 class="booking-confirmation-timeout__subheading" v-if="!bookingInternallyCancelled()">
              {{ $t('pages.bookingConfirmation.detailsHeadline') }}
            </h2>

            <div class="booking booking--summary warning" v-if="!bookingInternallyCancelled()">
              <section class="summary__step summary__step--no-border">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step1') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper" v-if="dates[0]">
                        <span class="label">{{ $t('generic.labels.arrival') }}: </span>
                        <span class="value">{{ getFormattedDate(dates[0]) }}</span>
                      </div>
                      <div class="wrapper" v-if="dates[1]">
                        <span class="label">{{ $t('generic.labels.departure') }}: </span>
                        <span class="value">{{ getFormattedDate(dates[1]) }}</span>
                      </div>
                      <div class="wrapper" v-if="dates[0] && dates[1]">
                        <span class="label">{{ $t('generic.labels.nights') }}: </span>
                        <span class="value">{{ numNights }}</span>
                      </div>

                      <div class="wrapper margin-top">
                        <div class="wrapper" v-for="(person, index) of getGuestData()" :key="index">
                          <span class="value">{{ person.amount }}x {{ person.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step" v-if="getUserCampsites">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step2') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value step__value--margin-bottom" v-for="(value, key) in getUserCampsites" :key="key">
                      <div class="value" v-if="Object.keys(getUserCampsites).length > 1">
                        {{ key }}
                        <small>({{ $t('generic.labels.nights') }}: {{ value[0].days }})</small>
                      </div>
                      <div class="wrapper d-flex justify-content-between no-padding" v-for="(campsite, index) in value" :key="index">
                        <span>{{ campsite.name }}</span>
                        <span class="prices">{{ campsite.price | currency(currency.code) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step summary__step--border-bottom" v-if="getGuestBySeason">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('pages.bookingSummary.priceDetailsHeadline') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value step__value--margin-bottom" v-for="(value, key) in getGuestBySeason" :key="key">
                      <div class="value" v-if="Object.keys(getGuestBySeason).length > 1">
                        {{ key }}
                        <small>({{ $t('generic.labels.nights') }}: {{ value[0].days }})</small>
                      </div>
                      <div class="wrapper d-flex justify-content-between no-padding" v-for="(person, index) in value" :key="index">
                        <span>{{ person.amount }}x {{ person.name }}</span>
                        <div class="prices">
                          <span class="price__value" v-if="!person.included">{{ person.price | currency(currency.code) }}</span>
                          <span class="price__value" v-if="person.included">{{ $t('generic.labels.included') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- subtotal start -->
              <section class="summary__sum d-flex justify-content-between align-items-center">
                <div class="sum-label">
                  {{ $t('generic.labels.subtotal') }}
                  <CiTooltip class="tooltip">
                    <template slot="title">{{ $t('tooltips.subtotal.title') }}</template>
                    <template slot="content">
                      <p v-html="$t('tooltips.subtotal.content')"></p>
                    </template>
                  </CiTooltip>
                </div>
                <div class="sum-value">
                  {{ price.subtotal | currency(currency.code) }}
                </div>
              </section>
              <!-- subtotal end -->

              <section class="summary__step" v-if="getUserExtras && getUserExtras.length > 0">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step3') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper d-flex justify-content-between no-padding" v-for="extra in getUserExtras" :key="extra.object_id">
                        <div class="label" v-html="extra.description"></div>
                        <div class="label" v-if="extra.price !== 0">{{ extra.price | currency(currency.code) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step" v-if="getMandatoryExtrasPriceNotZero && getMandatoryExtrasPriceNotZero.length > 0">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $tc('generic.labels.mandatory_extra', 0) }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper d-flex justify-content-between no-padding" v-for="mandatory in getMandatoryExtrasPriceNotZero" :key="mandatory.object_id">
                        <div class="label" v-html="mandatory.description"></div>
                        <div class="label" v-if="mandatory.price !== 0">{{ mandatory.price | currency(currency.code) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step4') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper" v-if="billing.name && billing.lastname">
                        {{ billing.name }} {{ billing.lastname }}
                      </div>
                      <div class="wrapper" v-if="billing.street">
                        {{ billing.street }}
                      </div>
                      <div class="wrapper" v-if="billing.street_additional">
                        {{ billing.street_additional }}
                      </div>
                      <div class="wrapper" v-if="billing.zip && billing.city">
                        {{ billing.zip }} {{ billing.city }}
                      </div>
                      <div class="wrapper" v-if="billing.country">
                        {{ billing.country.label }}
                      </div>
                      <div class="wrapper margin-top" v-if="billing.phone">
                        {{ billing.phone }}
                      </div>
                      <div class="wrapper" v-if="billing.mail">
                        {{ billing.mail }}
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="Object.keys(travellers).length > 0">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.travellers') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="Object.keys(travellers).length > 0">
                    <div class="step__value step__value--inter">
                      <div class="wrapper margin-top" v-for="(traveller, index) in travellers" :key="index">
                        <div>{{ traveller.first_name }} {{ traveller.last_name }}</div>
                        <span>{{ getFormattedDate(traveller.birthdate) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="car.license_plate || car.length || car.width">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.car') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="car.license_plate || car.length || car.width">
                    <div class="step__value step__value--inter">
                      <div class="wrapper">
                        <div v-if="car.license_plate">{{ car.license_plate }}</div>
                        <div v-if="car.length">{{ car.length }} {{ $t('generic.labels.length') }}</div>
                        <div v-if="car.width">{{ car.width }} {{ $t('generic.labels.width') }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="billing.message">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.message_to_campground') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="billing.message">
                    <div class="step__value step__value--inter">
                      {{ billing.message }}
                    </div>
                  </div>
                </div>
              </section>

              <section class="summary__step">
                <CiSum
                  :bookingFailed="true"
                  :campgroundCurrency="campground.currency"
                  :confirmation="confirmation"
                  :isCompleted="isCompleted"
                  :language="language"
                  :price="price"
                  :userCurrency="currency">
                </CiSum>
              </section>
              <div class="summary__warning" v-if="getMandatoryExtrasPriceZero && getMandatoryExtrasPriceZero.length > 0">
                <div class="warning-title">
                  <iconWarning class="icon"></iconWarning>
                  {{ $t('pages.checkoutStepThree.infoBox.copy') }}
                </div>
                <div class="warning-content">
                  <div v-for="mandatory in getMandatoryExtrasPriceZero" :key="mandatory.object_id">
                    <div class="label" v-html="mandatory.description"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import bookingDataMixin from '@/mixins/bookingDataMixin'
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import {
  CiButton,
  CiInfoBox,
  CiProgressBar,
  CiProgressNavigation,
  CiSum,
  CiTooltip,
} from '@/components'

import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'

export default {
  name: 'BookingConfirmation',
  mixins: [bookingDataMixin, bookingPriceMixin],
  components: {
    CiButton,
    CiInfoBox,
    CiProgressBar,
    CiProgressNavigation,
    CiSum,
    CiTooltip,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconWarning,
  },
  created() {
    this.setLoader(false)
  },
  computed: {
    ...mapGetters('application', {
      currency: 'currency',
      language: 'language',
    }),

    ...mapGetters('booking', {
      billing: 'billing',
      car: 'car',
      confirmation: 'confirmation',
      dates: 'dates',
      isCompleted: 'isCompleted',
      price: 'price',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      travellers: 'travellers',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
    }),

    supportEmail() {
      return process.env.VUE_APP_SUPPORT_EMAIL
    },
  },
  methods: {
    ...mapActions('application', {
      setLoader: 'setLoader',
      resetStepStatus: 'resetStepStatus',
    }),

    ...mapActions('booking', {
      resetBookingState: 'resetState',
    }),

    getGuestData() {
      return this.confirmation.booking_data.campsites[0].persons
    },

    bookingInternallyCancelled() {
      return this.confirmation.booking_status === 'internally_canceled'
    },

    startOver() {
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: 1 },
      })

      /* reset booking state */
      this.resetBookingState()

      /* reset step status */
      this.resetStepStatus()
    },

    getRefundNoSuccessEmailSubject() {
      return encodeURIComponent(`Refund not successful for Booking ${this.$route.params.bookingId}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.booking-confirmation-timeout {
  min-height: 100vh;

  @include media-breakpoint-up(lg) {
    > .container-fluid {
      padding: 0;
    }

    > .container-fluid,
    > .container-fluid > .row {
      min-height: 100vh;
    }
  }
}

.booking-confirmation-timeout__interaction {
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0;
  }

  @include media-breakpoint-up(lg) {
    padding: 0 30px 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 0 35px 0;
  }

  .booking__headline {
    @include font-format(1rem, 1rem);

    margin: 0 0 15px 0;
  }

  .booking__subline {
    margin: 0;
  }
}

.booking-confirmation-timeout__subheading {
  @include font-format(1.625rem, 1.875rem);

  margin: 40px 0 20px;
  text-align: center;
}

.booking {
  margin: 0;
  padding: 20px;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 35px;
  }

  &.booking--summary {
    padding-top: 10px;
    margin-bottom: 40px;
    &.warning {
      background-color: rgba(248, 231, 28, 0.15);
      border: 1px solid #f8e71c;
      border-radius: 4px;
      box-shadow: 0 2px 23px 0 #ededed;
    }
  }
}

.summary__step {
  @include font('gotham_bookregular');
  @include font-format(0.875rem, 1.125rem);

  border-top: 1px solid #979797;
  padding: 25px 0;
  position: relative;

  .step__label {
    @include font('gotham_mediumregular');
    @include font-format(0.875rem, 1.125rem);

    margin: 0 0 20px 0;

    &.step__label--inter {
      @include font('gotham_bookregular');

      margin: 0;
    }

    &.step__label--top {
      margin-top: 20px;
    }
  }

  .step__value {
    &.step__value--inter {
      margin-top: 20px;
    }
    &.step__value--margin-bottom {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .wrapper {
      padding-right: 40px;

      @include media-breakpoint-up(md) {
        padding-right: 70px;
      }

      &.margin-top {
        margin-top: 20px;
      }

      &.no-padding {
        padding: 0;
      }
    }

    .value {
      @include font('gotham_mediumregular');
      @include font-format(0.875rem, 1.125rem);

      small {
        @include font('gotham_bookregular');
      }
    }

    .prices {
      display: block;
    }

    .season__label {
      margin-bottom: 10px;
    }
  }

  .step__edit {
    position: absolute;
    top: 20px;
    right: 0;
    cursor: pointer;
  }

  &.summary__step--border-bottom {
    border-bottom: 1px solid #979797;
  }

  &.summary__step--no-border {
    border: 0;
  }
}

.summary__sum {
  background-color: rgba(155, 155, 155, 0.1);
  height: 50px;
  margin: 10px 0;

  .sum-label {
    display: flex;
    align-items: center;
    margin-left: 15px;
    height: 100%;
  }

  .sum-value {
    margin-right: 15px;
  }
}

.sum.sum--completed {
  background-color: rgba(248, 231, 28, 0.4);
  color: black;
  align-items: center;
}

.summary__warning {
  @include font('gotham_bookregular');
  @include font-format(0.75rem, 1.25rem);

  background-color: #fefcdd;
  border: 1px solid #f8e71c;
  margin: 15px 0 0 0;

  .warning-title {
    position: relative;
    background-color: #f8e71c66;
    padding: 10px 25px 10px 60px;

    .icon {
      position: absolute;
      left: 20px;
      top: 10px;
      width: 20px;
      height: auto;
    }
  }

  .warning-content {
    padding: 10px 25px 10px 60px;
  }
}
</style>
